/**
 * AutoCreateWizard — multi-screen container for the live Auto Create
 * pipeline. Mounted by AgentsPage once a run is started; subscribes to
 * the SSE event stream via useAutoCreateRun and switches between four
 * screens based on the run's lifecycle:
 *
 *   running           → PipelineScreen   (7 phase chips + live feed)
 *   paused_gap        → CapabilityGapScreen
 *   awaiting_review   → AgentDefReviewScreen
 *   completed/failed/cancelled/rejected → terminal panel + close
 *
 * Reference: AUTO_CREATE_AGENT_MOCKUP.html screens 4–7.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle, AlertTriangle, RotateCw, Sparkles, X,
  ArrowRight, GitBranch, BookOpen, FileText, Zap, ChevronRight,
} from 'lucide-react';
import { useAutoCreateRun } from '../../hooks/useAutoCreateRun';
import { useGapResolution } from '../../hooks/useGapResolution';
import {
  submitAutoCreateReview, cancelAutoCreateRun,
  getWorkflows, searchSkillRegistry,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
/* Phase 5C — Migrated from v1 Modal to v2 Slideout per G378 / V2
 * design rules. The wizard is a multi-screen, SSE-driven flow with
 * phase chips, gap resolution, agent-def review, and terminal
 * states — definitively slideout-shaped now that
 * AutoCreateLauncher already opens as a slideout. The internal
 * Pipeline/CapabilityGap/AgentDefReview/Terminal screens are
 * untouched; only the outer shell swaps. The legacy Modal import
 * from '../common/Modal' is retained nowhere — every consumer is
 * the v2 Slideout. */
import { Slideout } from '../v2/Slideout';

// ─── Shared style tokens (match AgentsPage palette) ─────────────────
const colors = {
  ink:    '#1c1917',
  ink2:   '#52525b',
  ink3:   '#78716c',
  ink4:   '#a8a29e',
  line:   '#e7e5e4',
  bg:     '#fafaf9',
  panel:  '#ffffff',
  primary:    '#0d9488',
  primary2:   '#e3f4f1',
  primary3:   '#a5d8d0',
  okBg:   '#dcfce7',
  okBd:   '#bbf7d0',
  okFg:   '#166534',
  warnBg: '#fffbeb',
  warnBd: '#fde68a',
  warnFg: '#854d0e',
  dangBg: '#e3f4f1',
  dangBd: '#a5d8d0',
  dangFg: '#7a2820',
  ctaDark1: '#1c1917',
  ctaDark2: '#292524',
};

const PHASE_DEFS = [
  { id: 'parse',   label: 'PARSE',   detail: 'Extract structured text' },
  { id: 'intent',  label: 'INTENT',  detail: 'LLM extracts trigger + steps' },
  { id: 'match',   label: 'MATCH',   detail: 'pgvector KNN against Skill Registry' },
  { id: 'gaps',    label: 'GAPS',    detail: 'Surface unmatched steps for review' },
  { id: 'draft',   label: 'DRAFT',   detail: 'Assemble AgentDefinition JSON' },
  { id: 'guards',  label: 'GUARDS',  detail: 'Compose guardrails + escalations' },
  { id: 'review',  label: 'REVIEW',  detail: 'Validator + human sign-off' },
];

// ────────────────────────────────────────────────────────────────────
// AutoCreateWizard — top-level
// ────────────────────────────────────────────────────────────────────

export default function AutoCreateWizard({ runId, onClose }) {
  const live = useAutoCreateRun(runId);

  // Choose screen by run status. The wizard NEVER closes itself —
  // even after terminal states, the user explicitly clicks Close so
  // they can read the final feed lines.
  let body;
  if (live.status === 'paused_gap') {
    body = <CapabilityGapScreen live={live} runId={runId} />;
  } else if (live.status === 'awaiting_review') {
    body = <AgentDefReviewScreen live={live} runId={runId} onClose={onClose} />;
  } else {
    body = <PipelineScreen live={live} runId={runId} onClose={onClose} />;
  }

  /* Phase 5C — Slideout shell. Notes on the swap from v1 Modal:
   *   - `wide` (720px in v1 Modal) becomes width={820}; the slideout
   *     is right-rail anchored so a touch more width keeps the
   *     7-phase chip row + live feed legible.
   *   - `title` becomes `header={...}` rendered as a richer block —
   *     the run status pill is pulled out so it's always visible
   *     even when the body scrolls past its own header row.
   *   - `actions={null}` ⟹ `footer={null}` — each inner screen
   *     (PipelineScreen / CapabilityGapScreen / AgentDefReviewScreen)
   *     still renders its own action buttons inline, exactly as
   *     before. We don't promote them into the slideout footer
   *     because the action set varies per screen and the inner
   *     screens already have well-tested action layouts; lifting
   *     them would be a separate refactor.
   *   - onClose semantics unchanged. The wizard never auto-closes;
   *     the user explicitly clicks Close so they can read the
   *     terminal feed lines, which we preserve. */
  return (
    <Slideout
      isOpen={true}
      onClose={onClose}
      width={820}
      ariaLabel={titleFor(live)}
      header={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: colors.ink }}>
            {titleFor(live)}
          </h2>
          {live?.status && (
            <span style={badgeStyle(live.status)}>{live.status}</span>
          )}
        </div>
      }
      footer={null}
    >
      {body}
    </Slideout>
  );
}

function titleFor(live) {
  switch (live.status) {
    case 'paused_gap':     return 'Auto Create — Capability Gap';
    case 'awaiting_review':return 'Auto Create — Review & Commit';
    case 'completed':      return 'Auto Create — Completed';
    case 'failed':         return 'Auto Create — Failed';
    case 'cancelled':      return 'Auto Create — Cancelled';
    case 'rejected':       return 'Auto Create — Rejected';
    default:               return 'Auto Creation Pipeline';
  }
}

// ────────────────────────────────────────────────────────────────────
// PipelineScreen — 7 phase chips + live feed (Screen 4)
// ────────────────────────────────────────────────────────────────────

function PipelineScreen({ live, runId, onClose }) {
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    setCancelling(true);
    try { await cancelAutoCreateRun(runId); }
    catch (e) { /* swallow — UI already shows feed line on cancel event */ }
  };

  const isTerminal = ['completed', 'failed', 'cancelled', 'rejected'].includes(live.status);
  const fileMeta = live.run ? (
    <span style={{ fontSize: 12, color: colors.ink3 }}>
      {live.run.sourceFilename || 'runbook'}
      {live.run.proposedAgentName ? ` · proposed: ${live.run.proposedAgentName}` : ''}
    </span>
  ) : null;

  // ── Success-state navigation: when the run completes, the agent
  //    has been persisted and is staged in the BUILD phase. The
  //    wizard's job from this point is to hand the user off cleanly
  //    — without this banner, they're left staring at a green
  //    pipeline with only a "Close" button and no obvious path to
  //    the agent they just created. The agentId comes from
  //    {@code AutoCreateRun.draftedAgentId} which the publishAgent
  //    activity stamps on the run row before emitting RUN_COMPLETED.
  //    The companion linked workflow id (if auto-create succeeded
  //    in creating it) is read from the same run's audit chain
  //    when available — the agent page surfaces it as the
  //    "Open in Canvas" affordance.
  const completedAgentId   = live.status === 'completed'
                             ? (live.run?.draftedAgentId || live.run?.agentId)
                             : null;
  const completedAgentName = live.status === 'completed'
                             ? (live.run?.proposedAgentName
                                || (live.run?.draftedDefinition?.agent?.name)
                                || 'AutoLets-Agent')
                             : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header row.
        * G79 — replaced the duplicate "Auto Creation Pipeline" sub-
        * heading with a descriptive run-meta line. The modal title
        * (set by titleFor() above) already says "Auto Creation
        * Pipeline" / "Auto Create — Capability Gap" / "Auto Create —
        * Failed" etc., so repeating the same words below was wasted
        * vertical space. The new subheading describes the SOURCE +
        * proposed name + run id so the user can correlate against
        * the audit trail. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 650, fontSize: 13, color: colors.ink, letterSpacing: '-0.01em' }}>
            {live.run?.sourceFilename
              ? <>From <code style={{ fontFamily: 'IBM Plex Mono, ui-monospace, monospace', fontSize: 12, color: colors.ink, background: colors.bg, border: `1px solid ${colors.line}`, padding: '1px 6px', borderRadius: 4 }}>{live.run.sourceFilename}</code></>
              : 'Pipeline run'}
            {live.run?.proposedAgentName ? <span style={{ fontWeight: 500, color: colors.ink2 }}> · proposed <strong style={{ color: colors.ink, fontWeight: 650 }}>{live.run.proposedAgentName}</strong></span> : null}
          </div>
          {runId && (
            <div style={{ fontSize: 11, color: colors.ink4, fontFamily: 'IBM Plex Mono, ui-monospace, monospace', marginTop: 2 }}>
              run {String(runId).slice(0, 8)}…{String(runId).slice(-4)}
            </div>
          )}
        </div>
        <span style={badgeStyle(live.status)}>{live.status}</span>
      </div>

      {/* ── Success banner shown only on `completed` with an agentId.
            Mirrors the post-Accept banner inside AgentDefReviewScreen
            so users see the same success affordance regardless of
            which screen surfaced the completion (e.g. fast Accept
            vs. polling-into-completed). ─── */}
      {completedAgentId && (
        <div style={{
          background: colors.okBg,
          border: `1px solid ${colors.okBd}`,
          borderRadius: 8,
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}>
          <CheckCircle size={18} style={{ color: colors.okFg, flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 650, color: '#14532d', marginBottom: 4 }}>
              Agent created · {completedAgentName}
            </div>
            <p style={{ fontSize: 12, color: colors.okFg, margin: 0, lineHeight: 1.55 }}>
              The agent is staged in the <strong>Build</strong> phase. Open it to
              review the materialised skill graph, validator findings, and proposed
              bindings — and to test-run the linked workflow before promoting through
              Decide → Act.
            </p>
          </div>
          {/*
            V168 — Two CTAs: Build phase (primary) + Canvas CEG (secondary).
            The Canonical Execution Graph link is essential for the AutoLets
            UX — without it users land in Build with no way to inspect the
            actual workflow that was synthesised. The /canvas?agentId= path
            auto-loads the linked CEG (CanvasPageV3 useEffect §V168 patch).
          */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <a
              href={`/agents?agentId=${encodeURIComponent(completedAgentId)}`}
              style={{
                ...btnPrimary,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              View {completedAgentName} in Build →
            </a>
            <a
              href={`/canvas?agentId=${encodeURIComponent(completedAgentId)}`}
              style={{
                ...btnGhost,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                fontSize: 12,
                textAlign: 'center',
              }}
              title="Open the Canonical Execution Graph for this agent in Workflow Canvas"
            >
              Open Execution Graph in Canvas →
            </a>
          </div>
        </div>
      )}

      {/* Phase chips */}
      <PhaseChips phases={live.phases} />

      {/* Two-column layout: feed + governance posture */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14 }}>
        <FeedPanel feed={live.feed} reconnecting={live.reconnecting} />
        <GovernancePanel live={live} />
      </div>

      {/* Action row */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {!isTerminal && (
          <button
            type="button"
            onClick={handleCancel}
            disabled={cancelling}
            style={{ ...btnGhost, opacity: cancelling ? 0.6 : 1 }}
          >
            {cancelling ? 'Cancelling…' : 'Cancel pipeline'}
          </button>
        )}
        <button type="button" onClick={onClose} style={btnGhost}>
          {isTerminal ? 'Close' : 'Run in background'}
        </button>
      </div>
    </div>
  );
}

function PhaseChips({ phases }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
      gap: 6, padding: '6px 0',
    }}>
      {PHASE_DEFS.map((def) => {
        const p = phases[def.id] || { status: 'pending' };
        const palette = chipPalette(p.status);
        return (
          <div key={def.id} style={{
            background: palette.bg, border: `1px solid ${palette.bd}`,
            borderRadius: 6, padding: '8px 10px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            position: 'relative',
          }} title={def.detail}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: palette.dot,
              animation: p.status === 'running' ? 'pulse 1.2s ease-in-out infinite' : 'none',
            }} />
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              color: palette.fg,
            }}>{def.label}</div>
            <div style={{ fontSize: 9, color: colors.ink4, fontFamily: 'IBM Plex Mono, ui-monospace, monospace' }}>
              {p.durationMs ? `${(p.durationMs / 1000).toFixed(1)}s` : '—'}
            </div>
          </div>
        );
      })}
      <style>{'@keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }'}</style>
    </div>
  );
}

function chipPalette(status) {
  switch (status) {
    case 'done':    return { bg: colors.okBg,   bd: colors.okBd,   fg: colors.okFg,   dot: colors.okFg };
    case 'running': return { bg: colors.primary2, bd: colors.primary3, fg: colors.primary, dot: colors.primary };
    case 'blocked': return { bg: colors.warnBg, bd: colors.warnBd, fg: colors.warnFg, dot: colors.warnFg };
    default:        return { bg: colors.bg,     bd: colors.line,   fg: colors.ink3,   dot: colors.ink4 };
  }
}

function FeedPanel({ feed, reconnecting }) {
  const scroll = React.useRef(null);
  React.useEffect(() => {
    if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [feed.length]);

  return (
    <div style={{
      background: '#0f0f0e', borderRadius: 8, padding: '12px 14px',
      fontFamily: 'IBM Plex Mono, ui-monospace, monospace', fontSize: 11,
      color: '#d6d3d1', height: 320, overflowY: 'auto', position: 'relative',
    }} ref={scroll}>
      {reconnecting && (
        <div style={{
          position: 'absolute', top: 8, right: 12, fontSize: 10,
          color: '#fbbf24', display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RotateCw size={10} style={{ animation: 'spin 1s linear infinite' }} />
          reconnecting…
        </div>
      )}
      {feed.length === 0 && (
        <div style={{ color: '#a8a29e', fontStyle: 'italic' }}>
          Waiting for the first phase to start…
        </div>
      )}
      {feed.map((e) => (
        <div key={e.sequence} style={{ marginBottom: 2, lineHeight: 1.55 }}>
          <span style={{ color: feedColor(e.level) }}>
            {feedGlyph(e.level)} {e.message || (e.eventType || '').replace(/_/g, ' ')}
          </span>
        </div>
      ))}
    </div>
  );
}

function feedColor(level) {
  switch (level) {
    case 'ok':   return '#86efac';
    case 'warn': return '#fbbf24';
    case 'run':  return '#93c5fd';
    case 'dim':  return '#a8a29e';
    default:     return '#d6d3d1';
  }
}
function feedGlyph(level) {
  switch (level) {
    case 'ok':   return '✓';
    case 'warn': return '⚠';
    case 'run':  return '⏵';
    case 'dim':  return '›';
    default:     return '·';
  }
}

function GovernancePanel({ live }) {
  const matched = live.run?.skillMatches?.stats?.matched ?? '—';
  const gapped  = live.run?.skillMatches?.stats?.gapped ?? '—';
  return (
    <div style={{
      background: colors.panel, border: `1px solid ${colors.line}`,
      borderRadius: 8, padding: '14px 16px', fontSize: 12, color: colors.ink2,
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div>
        <Label>Phase</Label>
        <div style={{ fontWeight: 600, color: colors.ink, marginTop: 4 }}>
          BUILD · locked
        </div>
      </div>
      <div>
        <Label>Source</Label>
        <div style={{ marginTop: 4 }}>
          {live.run?.sourceFilename || '—'}
        </div>
        {live.run?.sourceSha256 && (
          <div style={{
            fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
            fontSize: 10, color: colors.ink3, marginTop: 2,
          }}>
            sha256 {String(live.run.sourceSha256).slice(0, 16)}…
          </div>
        )}
      </div>
      <div>
        <Label>Skill matching</Label>
        <div style={{ marginTop: 4 }}>
          <strong style={{ color: colors.okFg }}>{matched}</strong> matched
          {' · '}
          <strong style={{ color: colors.warnFg }}>{gapped}</strong> gapped
        </div>
      </div>
      <div>
        <Label>Run ID</Label>
        <div style={{
          fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
          fontSize: 10, color: colors.ink3, marginTop: 4,
        }}>
          {live.run?.runId || '—'}
        </div>
      </div>
    </div>
  );
}
function Label({ children }) {
  return (
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: 0.6, color: colors.ink4,
      textTransform: 'uppercase',
    }}>{children}</div>
  );
}

// ────────────────────────────────────────────────────────────────────
// CapabilityGapScreen — Screen 5 (gap interstitial)
// ────────────────────────────────────────────────────────────────────

function CapabilityGapScreen({ live, runId }) {
  const gaps = live.pausedGap || {};
  const stepIds = Object.keys(gaps);

  /* The decision — which steps wait, what a resolution is, when they are all
     answered, and the payload that resumes the run — belongs to
     hooks/useGapResolution.js. The build trail asks the same question from a
     different surface and must get the same answer; two copies of a payload
     contract disagree the first time either is edited (Principle 8).

     What stays here is the rendering. GapRow draws from this file's v2 palette
     (line 38) and the trail is drawn in p7, so the paint is deliberately not
     shared. */
  const {
    resolutions: resolutionByStep, allResolved, choose, submit, submitting, error,
  } = useGapResolution({ gaps, runId });

  const handleSubmit = async () => {
    // The hook refuses while anything is unanswered, keeps `submitting` set on
    // success so the same decisions cannot be sent twice, and clears it on
    // failure so the operator can try again. The SSE stream re-renders the
    // wizard as run.status flips back to 'running'.
    await submit();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{
        background: colors.warnBg, border: `1px solid ${colors.warnBd}`,
        borderRadius: 8, padding: '12px 14px', fontSize: 12, color: colors.warnFg,
      }}>
        <strong>Pipeline paused.</strong> {stepIds.length} step{stepIds.length === 1 ? '' : 's'} need your
        decision — unmatched steps plus auto-bound skills to confirm. Keep the suggested skill, pick another,
        or mint a new one. Whatever you choose is hash-chained into the run's audit envelope.
      </div>

      {stepIds.map((stepId) => (
        <GapRow
          key={stepId}
          stepId={stepId}
          gap={gaps[stepId]}
          resolution={resolutionByStep[stepId]}
          onChange={(r) => choose(stepId, r)}
        />
      ))}

      {error && (
        <div style={{
          background: colors.dangBg, border: `1px solid ${colors.dangBd}`,
          borderRadius: 6, padding: '10px 12px', fontSize: 12, color: colors.dangFg,
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!allResolved || submitting}
          style={{
            ...btnPrimary,
            opacity: !allResolved || submitting ? 0.5 : 1,
            cursor: !allResolved || submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Submitting…' : `Resume pipeline (${Object.keys(resolutionByStep).length}/${stepIds.length} resolved)`}
        </button>
      </div>
    </div>
  );
}

function GapRow({ stepId, gap, resolution, onChange }) {
  const candidates = gap?.candidates || [];
  const action = resolution?.action;

  // ── Browse Skill Registry — inline search picker (operator preference is
  // inline over modal). Lets the reviewer bind ANY existing skill, not just the
  // matcher's top-3 candidates. Selecting a result produces the same use_match
  // resolution the candidate radios use, so the publish path is unchanged.
  const [browsing, setBrowsing] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [searchErr, setSearchErr] = React.useState(null);

  // Seed the query with the step's title the first time the picker opens.
  const openBrowse = () => {
    setBrowsing((b) => {
      const next = !b;
      if (next && !query) setQuery(gap?.title || '');
      return next;
    });
  };

  React.useEffect(() => {
    if (!browsing) return undefined;
    const q = (query || '').trim();
    if (q.length < 2) { setResults([]); setSearching(false); return undefined; }
    let cancelled = false;
    setSearching(true);
    setSearchErr(null);
    const handle = setTimeout(async () => {
      try {
        // apiFetch resolves to { ok, data, etag }; failures come back as
        // { ok:false, error } rather than throwing.
        const res = await searchSkillRegistry({ query: q, k: 8 });
        if (cancelled) return;
        if (res && res.ok === false) {
          setSearchErr(res.error || 'Search failed');
          setResults([]);
        } else {
          const items = res?.data?.items;
          setResults(Array.isArray(items) ? items : []);
        }
      } catch (e) {
        if (!cancelled) setSearchErr(e?.message || 'Search failed');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [browsing, query]);

  const pickFromBrowse = (item) => {
    onChange({
      action: 'use_match',
      registryId: item.id,
      displayName: item.displayName,
      kind: item.kind,
      connectorId: item.connectorId || null,
      source: 'user_resolution',
    });
    setBrowsing(false);
  };

  return (
    <div style={{
      border: `1px solid ${colors.line}`, borderRadius: 8,
      padding: '14px 16px', background: colors.panel,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <strong style={{ color: colors.ink }}>{stepId}</strong>
        {gap?.title && <span style={{ fontSize: 12, color: colors.ink3 }}>· {gap.title}</span>}
        <span style={{ fontSize: 10, fontWeight: 700,
          color: gap?.reviewKind === 'confirm_bind' ? colors.ink2 : colors.warnFg,
          background: gap?.reviewKind === 'confirm_bind' ? colors.primary2 : colors.warnBg,
          padding: '2px 6px', borderRadius: 4,
          textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {gap?.reviewKind === 'confirm_bind' ? 'Confirm' : 'Gap'}
        </span>
      </div>
      {gap?.prose && (
        <div style={{ fontSize: 11.5, color: colors.ink3, marginBottom: 8, lineHeight: 1.45 }}>
          {gap.prose}
        </div>
      )}

      {candidates.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <Label>Top candidates from Skill Registry</Label>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {candidates.map((c) => (
              <label key={c.registryId} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', borderRadius: 6,
                background: resolution?.registryId === c.registryId ? colors.primary2 : 'transparent',
                border: `1px solid ${resolution?.registryId === c.registryId ? colors.primary3 : 'transparent'}`,
                cursor: 'pointer', fontSize: 12,
              }}>
                <input
                  type="radio"
                  name={`gap_${stepId}`}
                  checked={resolution?.action === 'use_match' && resolution?.registryId === c.registryId}
                  onChange={() => onChange({
                    action: 'use_match',
                    registryId: c.registryId,
                    displayName: c.displayName,
                    kind: c.kind,
                    source: 'user_resolution',
                  })}
                />
                <code style={{ fontSize: 11, color: colors.ink2, fontFamily: 'IBM Plex Mono, ui-monospace, monospace' }}>
                  {c.registryId}
                </code>
                <span style={{ color: colors.ink3 }}>· {c.displayName}</span>
                {c.autoBound && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: colors.ink2,
                    background: colors.primary2, padding: '1px 5px', borderRadius: 3,
                    textTransform: 'uppercase', letterSpacing: 0.5 }}>current</span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 10, color: colors.ink3 }}>
                  {c.confidence}% confidence
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {action === 'use_match' && !candidates.some((c) => c.registryId === resolution?.registryId) && (
        <div style={{ fontSize: 11, color: colors.ink2, marginBottom: 8 }}>
          ✓ Will bind: <strong>{resolution?.displayName || resolution?.registryId}</strong>{' '}
          <code style={{ fontSize: 10, color: colors.ink3 }}>{resolution?.registryId}</code>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <ActionBtn
          label="✦ Mint a new skill for this step"
          active={action === 'mint_derived'}
          onClick={() => onChange({ action: 'mint_derived', source: 'user_resolution' })}
        />
        <ActionBtn
          label={browsing ? 'Close registry search' : 'Browse Skill Registry…'}
          active={browsing}
          onClick={openBrowse}
        />
        <ActionBtn
          label="Skip step"
          active={action === 'skip_step'}
          onClick={() => onChange({ action: 'skip_step', source: 'user_resolution' })}
        />
      </div>
      {action === 'mint_derived' && (
        <div style={{ fontSize: 11, color: colors.ink3, marginTop: 6 }}>
          A faithful AI-derived skill named after this step will be created and bound — use this
          when no existing skill matches (the agent reasons over it as a tool).
        </div>
      )}

      {browsing && (
        <div style={{
          marginTop: 10, padding: '10px 12px', borderRadius: 8,
          border: `1px solid ${colors.line}`, background: colors.bg2 || colors.panel,
        }}>
          <Label>Search the full Skill Registry</Label>
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. create incident, send email, isolate host…"
            style={{
              width: '100%', marginTop: 6, padding: '7px 10px', fontSize: 12,
              borderRadius: 6, border: `1px solid ${colors.line}`,
              background: colors.panel, color: colors.ink,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4,
            maxHeight: 220, overflowY: 'auto' }}>
            {searching && <div style={{ fontSize: 11, color: colors.ink3 }}>Searching…</div>}
            {searchErr && <div style={{ fontSize: 11, color: colors.dangFg }}>{searchErr}</div>}
            {!searching && !searchErr && (query || '').trim().length >= 2 && results.length === 0 && (
              <div style={{ fontSize: 11, color: colors.ink3 }}>No skills match “{query}”. Try other terms, or mint a new skill.</div>
            )}
            {results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => pickFromBrowse(r)}
                style={{
                  textAlign: 'left', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 8px', borderRadius: 6, fontSize: 12,
                  background: resolution?.registryId === r.id ? colors.primary2 : 'transparent',
                  border: `1px solid ${resolution?.registryId === r.id ? colors.primary3 : colors.line}`,
                  color: colors.ink2,
                }}
                title={r.description || ''}
              >
                <code style={{ fontSize: 11, color: colors.ink2,
                  fontFamily: 'IBM Plex Mono, ui-monospace, monospace' }}>{r.id}</code>
                <span style={{ color: colors.ink3 }}>· {r.displayName}</span>
                {r.kind && (
                  <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700, color: colors.ink3,
                    background: colors.panel, border: `1px solid ${colors.line}`,
                    padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase',
                    letterSpacing: 0.4 }}>{String(r.kind).replace('_skill', '').replace('_', ' ')}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 10, color: colors.ink3, marginTop: 8, fontStyle: 'italic' }}>
        Whatever you pick is recorded with your user id, timestamp, and the runbook paragraph
        in the GEL audit envelope.
      </div>
    </div>
  );
}

function ActionBtn({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...btnGhost,
        background: active ? colors.primary2 : colors.panel,
        borderColor: active ? colors.primary3 : colors.line,
        color: active ? colors.dangFg : colors.ink2,
      }}>
      {label}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// AgentDefReviewScreen — Screen 7 (final review + commit)
// ────────────────────────────────────────────────────────────────────

function AgentDefReviewScreen({ live, runId, onClose }) {
  /* G79 — read session ids so the workflow-collision pre-flight
   * check uses the SAME scope shape v1's PlatformContext uses. The
   * empty-scope `getWorkflows({})` call we ran before returned 0
   * rows for non-Super-Admin tenants; without scope the rename
   * never fired and the user kept hitting GOLDEN_CEG_RULES §1. */
  const { session } = useAuth();
  const _orgId = session?.orgId;
  const _tenantId = session?.tenantId;
  // Source of truth: the full AgentDefinition the drafter emitted.
  // We never lose it — it's just split into two views below so the
  // user sees and validates the strict Canvas shape primarily, and
  // the agent-level metadata (skills, guardrails, decision matrix)
  // separately.
  const fullDefinition = useMemo(() => {
    return live.reviewPayload?.definition || live.run?.draftedDefinition || null;
  }, [live.reviewPayload, live.run]);

  // Canvas-shape JSON — the slice that pastes 1:1 into Workflow
  // Canvas. {name, version, description, nodes, connections} —
  // nothing else. This is what the user sees and edits as the
  // primary view. Strict Canvas-shape validation runs on this.
  const initialCanvasJson = useMemo(() => {
    if (!fullDefinition) return '';
    if (fullDefinition.canvasDefinition) {
      return JSON.stringify(fullDefinition.canvasDefinition, null, 2);
    }
    if (fullDefinition.dag && Array.isArray(fullDefinition.dag.nodes)) {
      return JSON.stringify({
        // RC-47 — Canonical CEG naming (was "AutoLets Canvas · …").
        // Matches the BE's SEAL + rederive paths so the wizard's
        // JSON preview, BE row, and audit feed all use one term.
        name: `AutoLet CEG · ${fullDefinition.agent?.name || 'Untitled'}`,
        version: 2,
        description: `Auto-Created from ${fullDefinition.provenance?.sourceFilename || 'runbook'}`,
        nodes: fullDefinition.dag.nodes,
        connections: fullDefinition.dag.edges || [],
      }, null, 2);
    }
    return '';
  }, [fullDefinition]);

  // Agent-metadata JSON — everything in the AgentDefinition EXCEPT
  // dag and canvasDefinition. The user can edit skill bindings,
  // guardrail hints, decision matrix, escalations etc. without
  // touching the canvas shape. Hidden behind a toggle by default.
  const initialMetadataJson = useMemo(() => {
    if (!fullDefinition) return '';
    const { dag, canvasDefinition, ...rest } = fullDefinition; // eslint-disable-line no-unused-vars
    return JSON.stringify(rest, null, 2);
  }, [fullDefinition]);

  const [editedCanvas,    setEditedCanvas]    = useState(initialCanvasJson);
  const [editedMetadata,  setEditedMetadata]  = useState(initialMetadataJson);
  const [showMetadata,    setShowMetadata]    = useState(false);
  const [canvasError,     setCanvasError]     = useState(null);
  const [metadataError,   setMetadataError]   = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [canvasCopied, setCanvasCopied] = useState(false);

  const handleCopyCanvasJson = async () => {
    if (!editedCanvas) return;
    try {
      await navigator.clipboard.writeText(editedCanvas);
      setCanvasCopied(true);
      setTimeout(() => setCanvasCopied(false), 1800);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = editedCanvas;
      ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCanvasCopied(true); setTimeout(() => setCanvasCopied(false), 1800); }
      catch (_) {}
      document.body.removeChild(ta);
    }
  };

  const validation = live.reviewPayload?.validation || live.run?.validationResult;
  const blocking   = validation?.blocking || [];
  const warnings   = validation?.warnings || [];

  const tryParse = (text) => {
    try { return [JSON.parse(text), null]; }
    catch (e) { return [null, e.message]; }
  };

  /**
   * Strict Canvas-shape validation. Mirrors the backend
   * WorkflowDraftValidator's structural checks so the user gets
   * immediate feedback as they edit, instead of round-tripping
   * through the server.
   */
  const validateCanvasShape = (parsed) => {
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return 'Top-level value must be a Canvas workflow object';
    }
    if (typeof parsed.name !== 'string' || !parsed.name.trim()) {
      return 'Canvas requires a non-empty `name` (string)';
    }
    if (typeof parsed.version !== 'number') {
      return 'Canvas requires a `version` (number; canonical = 2)';
    }
    if (!Array.isArray(parsed.nodes)) return '`nodes` must be an array';
    if (!Array.isArray(parsed.connections)) return '`connections` must be an array';

    const nodeIds = new Set();
    const triggerTypes = new Set([
      'manual_start', 'schedule', 'webhook', 'siem_alert', 'email_trigger', 'system_event',
    ]);
    let triggerCount = 0;

    for (let i = 0; i < parsed.nodes.length; i++) {
      const n = parsed.nodes[i];
      if (!n || typeof n !== 'object') return `nodes[${i}] is not an object`;
      if (typeof n.id !== 'string' || !n.id) return `nodes[${i}].id must be a non-empty string`;
      if (nodeIds.has(n.id)) return `nodes[${i}] duplicates id "${n.id}"`;
      nodeIds.add(n.id);
      if (typeof n.type !== 'string' || !n.type) return `nodes[${i}].type must be a non-empty string`;
      if (typeof n.config !== 'object' || n.config === null || Array.isArray(n.config)) {
        return `nodes[${i}].config must be an object`;
      }
      if (triggerTypes.has(n.type)) triggerCount++;
    }

    if (triggerCount === 0) {
      return 'Canvas requires exactly one entry/trigger node (manual_start, siem_alert, email_trigger, webhook, schedule, or system_event)';
    }
    if (triggerCount > 1) {
      return `Canvas has ${triggerCount} trigger nodes; exactly one is allowed`;
    }

    for (let i = 0; i < parsed.connections.length; i++) {
      const c = parsed.connections[i];
      if (!c || typeof c !== 'object') return `connections[${i}] is not an object`;
      if (typeof c.from !== 'string' || !c.from) return `connections[${i}].from must be a non-empty string`;
      if (typeof c.to !== 'string' || !c.to)     return `connections[${i}].to must be a non-empty string`;
      if (!nodeIds.has(c.from)) return `connections[${i}].from references unknown node id "${c.from}"`;
      if (!nodeIds.has(c.to))   return `connections[${i}].to references unknown node id "${c.to}"`;
      // Port discipline. Required because the Canvas renderer
      // attaches edges to specific output ports per node type.
      if (typeof c.fromPort !== 'string' || !c.fromPort) {
        return `connections[${i}].fromPort is required (e.g. "out", "approved", "true")`;
      }
      if (typeof c.toPort !== 'string' || !c.toPort) {
        return `connections[${i}].toPort is required (canonical = "in")`;
      }
    }
    return null;
  };

  // Live shape-validation as the user types — re-runs on every
  // canvas-editor change so the red banner under the editor reflects
  // the current buffer.
  useEffect(() => {
    const [parsed, err] = tryParse(editedCanvas);
    if (err) { setCanvasError(`JSON parse: ${err}`); return; }
    setCanvasError(validateCanvasShape(parsed));
  }, [editedCanvas]);

  useEffect(() => {
    if (!showMetadata) { setMetadataError(null); return; }
    const [, err] = tryParse(editedMetadata);
    setMetadataError(err ? `JSON parse: ${err}` : null);
  }, [editedMetadata, showMetadata]);

  const handleAccept = async () => {
    // Parse both halves and reassemble the full AgentDefinition the
    // backend expects. The Canvas-edited nodes/connections become
    // the agent's dag.{nodes,edges}; the metadata stays untouched
    // unless the user opened that section and edited it.
    const [canvasParsed, canvasErr] = tryParse(editedCanvas);
    if (canvasErr) { setCanvasError(`JSON parse: ${canvasErr}`); return; }
    const shapeErr = validateCanvasShape(canvasParsed);
    if (shapeErr) { setCanvasError(shapeErr); return; }
    setCanvasError(null);

    let metadataParsed;
    if (showMetadata) {
      const [m, mErr] = tryParse(editedMetadata);
      if (mErr) { setMetadataError(`JSON parse: ${mErr}`); return; }
      metadataParsed = m;
      setMetadataError(null);
    } else {
      metadataParsed = JSON.parse(initialMetadataJson || '{}');
    }

    // Reassemble. Canvas edits flow back into the agent's `dag`
    // (preserving the schema field name) AND the `canvasDefinition`
    // companion (kept in sync so existing consumers of the paste
    // shape continue to work). Skills/guardrails/etc. flow from
    // metadataParsed.
    const merged = {
      ...metadataParsed,
      dag: {
        nodes: canvasParsed.nodes,
        edges: canvasParsed.connections,  // schema field is `edges`
      },
      canvasDefinition: canvasParsed,
      /* G79 idempotency hint — tell the backend it can reuse an
       * existing CEG with the same name instead of failing per
       * GOLDEN_CEG_RULES §1. Two flags so older backends that don't
       * recognise either still succeed (unknown fields are ignored)
       * and newer backends that recognise the verb form do the
       * right thing. The pre-flight rename below is the safety
       * net for backends that ignore both. */
      replaceExistingCeg: true,
      cegConflictPolicy: 'update_in_place',
    };

    /* G79 pre-flight name check + DEEP rename. The backend's
     * GOLDEN_CEG_RULES §1 rolls back the AutoLet entirely when a
     * workflow with the proposed CEG name already exists.
     *
     * The previous Round 16 cut renamed only `canvasDefinition.name`,
     * but the backend derives the CEG workflow name from MULTIPLE
     * places in the payload (canvas root, dag.name, agent.canvasName,
     * any nested copy). So if any of those still carried the original
     * name, the backend's `publishAgent` activity used THAT one and
     * we kept hitting the duplicate.
     *
     * The robust fix: stringify the entire payload, do a JSON-level
     * substitution of EVERY occurrence of the original name → the
     * renamed value, parse back. This guarantees nothing slips
     * through regardless of which field the backend reads. */
    if (canvasParsed?.name && typeof canvasParsed.name === 'string') {
      try {
        const wfRes = await getWorkflows({ orgId: _orgId, tenantId: _tenantId, archived: 'all' });
        const list = wfRes?.ok && Array.isArray(wfRes.data) ? wfRes.data
          : (Array.isArray(wfRes) ? wfRes : []);
        const originalName = canvasParsed.name.trim();
        const collision = list.find((w) => (w.name || '').trim() === originalName);
        if (collision) {
          const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          const renamed = `${originalName} · ${ts}`;
          /* Deep substitution — JSON-level string replacement so every
           * field that mirrors the original CEG name picks up the new
           * name. Escape regex metachars in the original. The literal
           * value never contains JSON-control chars (no quotes etc.
           * survive into a workflow name) so a global replace is safe. */
          const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const json = JSON.stringify(merged);
          const re = new RegExp(escapeRe(originalName), 'g');
          const replaced = json.replace(re, renamed);
          try {
            const reparsed = JSON.parse(replaced);
            // Preserve our hint flags + add the rename audit field.
            reparsed.replaceExistingCeg = true;
            reparsed.cegConflictPolicy = 'update_in_place';
            reparsed._cegRenamed = {
              from: originalName,
              to: renamed,
              reason: 'pre-existing CEG with the same name; renamed transparently to keep AutoLet creation idempotent',
            };
            // Mutate `merged` in place so the submit below uses it.
            Object.keys(merged).forEach((k) => { delete merged[k]; });
            Object.assign(merged, reparsed);
          } catch {
            /* If reparse fails (shouldn't — we only swapped strings),
             * fall back to the shallow rename. */
            merged.canvasDefinition = { ...canvasParsed, name: renamed };
            merged._cegRenamed = { from: originalName, to: renamed };
          }
        }
      } catch {
        /* If the lookup fails we fall through and let the backend
         * decide. Worst case the user sees the same duplicate-name
         * error they saw before this fix; there's no regression. */
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await submitAutoCreateReview(runId, true, merged);
      const body = res?.ok ? res.data : res;
      setCommitted(body);
    } catch (e) {
      setError(e?.message || 'Commit failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await submitAutoCreateReview(runId, false, null);
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Reject failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (committed?.agentId) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{
          background: colors.okBg, border: `1px solid ${colors.okBd}`,
          borderRadius: 8, padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <CheckCircle size={18} style={{ color: colors.okFg, flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 650, color: '#14532d', marginBottom: 4 }}>
              Agent created · {committed.agentName}
            </div>
            <p style={{ fontSize: 12, color: colors.okFg, margin: 0, lineHeight: 1.55 }}>
              The agent is staged in the <strong>Build</strong> phase. Open it to review the materialised
              skill graph, validator findings, and proposed bindings before promoting through DECIDE.
            </p>
            {/* G79 — when the pre-flight rename kicked in (existing CEG
              * with the same name), tell the user transparently. The
              * backend response carries `cegRenamed` if it noticed the
              * rename via the metadata flag we sent. The frontend can
              * also display a fallback derived from the committed run. */}
            {committed?.cegRenamed?.to && (
              <p style={{ fontSize: 11.5, color: colors.warnFg, margin: '6px 0 0', lineHeight: 1.5, background: colors.warnBg, border: `1px solid ${colors.warnBd}`, padding: '6px 8px', borderRadius: 6 }}>
                <strong>CEG renamed:</strong> a workflow named <code style={{ fontFamily: 'IBM Plex Mono, ui-monospace, monospace' }}>{committed.cegRenamed.from}</code> already existed in this tenant. The new CEG was committed as <code style={{ fontFamily: 'IBM Plex Mono, ui-monospace, monospace' }}>{committed.cegRenamed.to}</code> so the AutoLet could be created idempotently. The original workflow was left untouched.
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={onClose} style={btnGhost}>Close</button>
          {/* V168 — Canvas CEG link parallel to the Build link so the
              user can land directly in Workflow Canvas to inspect the
              auto-derived execution graph. */}
          <a
            href={`/canvas?agentId=${encodeURIComponent(committed.agentId)}`}
            style={{ ...btnGhost, textDecoration: 'none' }}
            title="Open the Canonical Execution Graph for this agent in Workflow Canvas"
          >
            Open in Canvas →
          </a>
          <a
            href={`/agents?agentId=${encodeURIComponent(committed.agentId)}`}
            style={{ ...btnPrimary, textDecoration: 'none' }}
          >
            View {committed.agentName} in Build →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ fontSize: 13, color: colors.ink2, margin: 0, lineHeight: 1.55 }}>
        Pipeline finished. Review the drafted AgentDefinition and validator findings below. Edit the JSON
        directly if needed; on accept the platform creates the Agent record + persists this definition.
      </p>

      <ValidationPanel blocking={blocking} warnings={warnings} canvasError={canvasError} />

      {/* ─── Canvas workflow section — primary editable view ─── */}
      <div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 6,
        }}>
          <Label>Canvas Workflow · strict shape validated</Label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 11, fontWeight: 600,
              padding: '2px 8px', borderRadius: 10,
              background: canvasError ? colors.dangBg : '#f0fdf4',
              border: `1px solid ${canvasError ? colors.dangBd : '#bbf7d0'}`,
              color: canvasError ? colors.dangFg : '#166534',
            }}>
              {canvasError ? 'Invalid' : 'Canvas-shape ✓'}
            </span>
            <button
              type="button"
              onClick={handleCopyCanvasJson}
              style={{
                ...btnGhost,
                padding: '4px 10px', fontSize: 11, fontWeight: 600,
                borderColor: '#7dd3fc', color: '#0369a1',
              }}
            >
              {canvasCopied ? '✓ Copied' : 'Copy for Canvas'}
            </button>
          </div>
        </div>
        <p style={{
          fontSize: 11, color: colors.ink2, margin: '0 0 6px 0', lineHeight: 1.45,
        }}>
          This is the strict <code>{`{name, version, description, nodes, connections}`}</code>{' '}
          shape Workflow Canvas accepts. Edit nodes, connections, ports, or
          config inline — the validator below runs on every keystroke.
          Click <strong>Copy for Canvas</strong> to paste it into a new workflow.
        </p>
        <textarea
          value={editedCanvas}
          onChange={(e) => setEditedCanvas(e.target.value)}
          spellCheck={false}
          style={{
            width: '100%', minHeight: 360,
            fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
            fontSize: 11, lineHeight: 1.55,
            background: '#0f0f0e', color: '#d6d3d1',
            border: `1px solid ${canvasError ? colors.dangBd : colors.line}`,
            borderRadius: 6, padding: 12, resize: 'vertical', boxSizing: 'border-box',
          }}
        />
        {canvasError && (
          <div style={{
            background: colors.dangBg, border: `1px solid ${colors.dangBd}`,
            borderRadius: 6, padding: '8px 10px', fontSize: 11, color: colors.dangFg,
            marginTop: 8,
          }}>
            <strong>Canvas validation failed:</strong> {canvasError}
          </div>
        )}
      </div>

      {/* ─── Agent metadata section — collapsed by default ─── */}
      <div>
        <button
          type="button"
          onClick={() => setShowMetadata(!showMetadata)}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 12, color: colors.ink2, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <span style={{
            display: 'inline-block', transition: 'transform 120ms ease',
            transform: showMetadata ? 'rotate(90deg)' : 'rotate(0deg)',
          }}>▶</span>
          Agent Metadata · skills, guardrails, decision matrix, escalations
          <span style={{ fontWeight: 400, color: colors.ink3, fontSize: 11 }}>
            ({showMetadata ? 'editable' : 'auto-derived from runbook'})
          </span>
        </button>
        {showMetadata && (
          <>
            <p style={{
              fontSize: 11, color: colors.ink2, margin: '6px 0', lineHeight: 1.45,
            }}>
              These fields are part of the AgentDefinition and govern{' '}
              the agent's runtime behavior, but they are <em>not</em>{' '}
              part of the Canvas workflow. Most users don't need to edit
              these — the runbook drafter has populated them already.
            </p>
            <textarea
              value={editedMetadata}
              onChange={(e) => setEditedMetadata(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%', minHeight: 240,
                fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
                fontSize: 11, lineHeight: 1.55,
                background: '#0f0f0e', color: '#d6d3d1',
                border: `1px solid ${metadataError ? colors.dangBd : colors.line}`,
                borderRadius: 6, padding: 12, resize: 'vertical', boxSizing: 'border-box',
              }}
            />
            {metadataError && (
              <div style={{
                background: colors.dangBg, border: `1px solid ${colors.dangBd}`,
                borderRadius: 6, padding: '8px 10px', fontSize: 11, color: colors.dangFg,
                marginTop: 8,
              }}>
                {metadataError}
              </div>
            )}
          </>
        )}
      </div>

      {error && (
        <div style={{
          background: colors.dangBg, border: `1px solid ${colors.dangBd}`,
          borderRadius: 6, padding: '10px 12px', fontSize: 12, color: colors.dangFg,
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" onClick={handleReject} disabled={submitting}
          style={{ ...btnGhost, color: colors.dangFg, borderColor: colors.dangBd }}>
          Reject
        </button>
        {(() => {
          const hasErrors = !!canvasError || !!metadataError || blocking.length > 0;
          const disabled = submitting || hasErrors;
          const title = canvasError
              ? `Canvas: ${canvasError}`
              : metadataError
                ? `Metadata: ${metadataError}`
                : blocking.length > 0
                  ? 'Resolve blocking validation issues first'
                  : '';
          return (
            <button type="button" onClick={handleAccept} disabled={disabled}
              style={{
                ...btnPrimary,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              title={title}
            >
              {submitting ? 'Creating agent…' : 'Accept & create agent'}
            </button>
          );
        })()}
      </div>
    </div>
  );
}

function ValidationPanel({ blocking, warnings, canvasError }) {
  // Fold the strict canvas-shape result into this summary. Without this the
  // panel reports the AgentDefinition validator only, so it would say
  // "passed cleanly — no blocking issues" while a blocking canvas-shape error
  // (e.g. "nodes[i].config must be an object") renders below — a contradiction.
  const effectiveBlocking = canvasError
    ? [{ path: 'canvas.shape', message: canvasError }, ...(blocking || [])]
    : (blocking || []);
  if (effectiveBlocking.length === 0 && (!warnings || warnings.length === 0)) {
    return (
      <div style={{
        background: colors.okBg, border: `1px solid ${colors.okBd}`,
        borderRadius: 6, padding: '8px 12px', fontSize: 12, color: colors.okFg,
      }}>
        Validator passed cleanly — no blocking issues, no warnings.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {effectiveBlocking.length > 0 && (
        <div style={{
          background: colors.dangBg, border: `1px solid ${colors.dangBd}`,
          borderRadius: 6, padding: '10px 12px',
        }}>
          <div style={{ fontSize: 12, fontWeight: 650, color: colors.dangFg, marginBottom: 4 }}>
            {effectiveBlocking.length} blocking issue{effectiveBlocking.length === 1 ? '' : 's'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: colors.dangFg, lineHeight: 1.5 }}>
            {effectiveBlocking.map((b, i) => <li key={i}><code>{b.path}</code>: {b.message}</li>)}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div style={{
          background: colors.warnBg, border: `1px solid ${colors.warnBd}`,
          borderRadius: 6, padding: '10px 12px',
        }}>
          <div style={{ fontSize: 12, fontWeight: 650, color: colors.warnFg, marginBottom: 4 }}>
            {warnings.length} warning{warnings.length === 1 ? '' : 's'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: colors.warnFg, lineHeight: 1.5 }}>
            {warnings.map((w, i) => <li key={i}><code>{w.path}</code>: {w.message}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Shared button styles ────────────────────────────────────────────
const btnGhost = {
  padding: '7px 14px', borderRadius: 8, border: `1px solid ${colors.line}`,
  background: colors.panel, cursor: 'pointer', fontSize: 12,
  display: 'inline-flex', alignItems: 'center', gap: 5,
  fontWeight: 500, fontFamily: 'inherit',
};
const btnPrimary = {
  ...btnGhost, border: 'none',
  background: `linear-gradient(135deg, ${colors.ctaDark1} 0%, ${colors.ctaDark2} 100%)`,
  color: '#fff', fontWeight: 600,
};

function badgeStyle(status) {
  const palette = {
    running:         { bg: colors.primary2, fg: colors.primary,  bd: colors.primary3 },
    paused_gap:      { bg: colors.warnBg,   fg: colors.warnFg,   bd: colors.warnBd },
    awaiting_review: { bg: colors.warnBg,   fg: colors.warnFg,   bd: colors.warnBd },
    completed:       { bg: colors.okBg,     fg: colors.okFg,     bd: colors.okBd },
    failed:          { bg: colors.dangBg,   fg: colors.dangFg,   bd: colors.dangBd },
    cancelled:       { bg: colors.bg,       fg: colors.ink3,     bd: colors.line },
    rejected:        { bg: colors.bg,       fg: colors.ink3,     bd: colors.line },
  }[status] || { bg: colors.bg, fg: colors.ink3, bd: colors.line };
  return {
    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
    background: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`,
    textTransform: 'uppercase', letterSpacing: 0.6,
  };
}
